import { useMemo } from "react"
import { useParams } from "react-router-dom"
import useUrlParams from "hooks/useUrlParams"
import classnames from "classnames"
import {
  Badge,
  Col,
  Container,
  Nav,
  NavItem,
  NavLink,
  Row,
  TabContent,
  TabPane,
} from "reactstrap"

import Breadcrumbs from "components/Common/Breadcrumb"
import DataCard from "components/Common/DataCard"
import { prepareDataWithLoading } from "helpers/loading_helper"
import {
  useAllEmployeeDepartments,
  useCreateEmployeeDepartment,
} from "hooks/useEmployeeDepartment"
import {
  useAllEmployeeDesignations,
  useCreateEmployeeDesignation,
} from "hooks/useEmployeeDesignation"
import { useEmployee, useEmployees } from "hooks/employeeHook"
import { useShowrooms } from "hooks/showroomHook"
import { useAuth } from "hooks/authHook"
import { ACCESS } from "constants/accessPermissions"
import { formatReadableDate } from "utils/formatDate"
import ActivityTab from "./ActivityTab"
import EmployeeInfo from "./EmployeeInfo"
import EmployeeProfileCard from "./EmployeeProfileCard"
import EmployeeSectionUpdate from "./EmployeeSectionUpdate"
import SystemAccessCard from "./SystemAccessCard"
import LoanSummaryCard from "pages/AccountPages/AccountLoan/LoanSummaryCard"

const EMPLOYEE_PROFILE_BASE_TABS = [
  "employment",
  "personal",
  "contacts",
  "access",
]

const STATUS_META = {
  active: { label: "Active", color: "success" },
  inactive: { label: "Inactive", color: "secondary" },
  on_leave: { label: "On Leave", color: "warning" },
  resigned: { label: "Resigned", color: "danger" },
  terminated: { label: "Terminated", color: "dark" },
}

const EMPLOYMENT_TYPE_OPTIONS = [
  { value: "permanent", label: "Permanent" },
  { value: "contractual", label: "Contractual" },
  { value: "probation", label: "Probation" },
  { value: "temporary", label: "Temporary" },
]

const STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
  { value: "on_leave", label: "On Leave" },
  { value: "resigned", label: "Resigned" },
  { value: "terminated", label: "Terminated" },
]

const GENDER_OPTIONS = [
  { value: "male", label: "Male" },
  { value: "female", label: "Female" },
]

const MARITAL_STATUS_OPTIONS = [
  { value: "single", label: "Single" },
  { value: "married", label: "Married" },
  { value: "divorced", label: "Divorced" },
  { value: "widowed", label: "Widowed" },
]

const BLOOD_GROUP_OPTIONS = [
  { value: "A+", label: "A+" },
  { value: "A-", label: "A-" },
  { value: "B+", label: "B+" },
  { value: "B-", label: "B-" },
  { value: "AB+", label: "AB+" },
  { value: "AB-", label: "AB-" },
  { value: "O+", label: "O+" },
  { value: "O-", label: "O-" },
]

const RELIGION_OPTIONS = [
  { value: "Islam", label: "Islam" },
  { value: "Hinduism", label: "Hinduism" },
  { value: "Buddhism", label: "Buddhism" },
  { value: "Christianity", label: "Christianity" },
  { value: "Other", label: "Other" },
]

const YES_NO_OPTIONS = [
  { value: "1", label: "Yes" },
  { value: "0", label: "No" },
]

const BANK_INFO_FIELDS = [
  { key: "bank_name", label: "Bank Name" },
  { key: "branch_name", label: "Branch Name" },
  { key: "account_name", label: "Account Name" },
  { key: "account_number", label: "Account Number" },
  { key: "routing_number", label: "Routing Number" },
  { key: "swift_code", label: "SWIFT Code" },
]

const humanize = value => {
  if (value === null || value === undefined || value === "") return "-"
  return String(value)
    .split("_")
    .join(" ")
    .replace(/\b\w/g, char => char.toUpperCase())
}

const formatMoney = value => {
  if (value === null || value === undefined || value === "") return "-"

  const number = Number(value)
  if (Number.isNaN(number)) return value

  return new Intl.NumberFormat("en-BD", {
    style: "currency",
    currency: "BDT",
    maximumFractionDigits: 0,
  }).format(number)
}

const formatDateValue = value => (value ? formatReadableDate(value) : "-")

const formatBoolean = value => (value ? "Yes" : "No")

const getBankInfoData = bankInfo => {
  if (!bankInfo || typeof bankInfo !== "object") return []

  const knownFields = BANK_INFO_FIELDS.map(field => ({
    label: field.label,
    value: bankInfo[field.key],
  })).filter(
    item => item.value !== null && item.value !== undefined && item.value !== ""
  )

  const knownKeys = new Set(BANK_INFO_FIELDS.map(field => field.key))
  const extraFields = Object.entries(bankInfo)
    .filter(
      ([key, value]) =>
        !knownKeys.has(key) &&
        value !== null &&
        value !== undefined &&
        value !== ""
    )
    .map(([key, value]) => ({
      label: humanize(key),
      value: typeof value === "object" ? JSON.stringify(value) : value,
    }))

  return [...knownFields, ...extraFields]
}

const buildBankInfoPayload = values => {
  const bankInfo = BANK_INFO_FIELDS.reduce((acc, field) => {
    const value = values[field.key]

    if (value !== null && value !== undefined && value !== "") {
      acc[field.key] = value
    }

    return acc
  }, {})

  return {
    bank_info: Object.keys(bankInfo).length ? bankInfo : null,
  }
}

const asCollection = value => {
  if (!value) return []
  return Array.isArray(value) ? value : [value]
}

const extractList = response => response?.data?.data ?? response?.data ?? []

const RichCollectionCard = ({
  title,
  icon,
  entries = [],
  emptyText,
  action = null,
  renderEntry,
}) => (
  <div className="card border-0 shadow-sm h-100">
    <div className="card-body p-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h6 className="mb-0 d-flex align-items-center fw-semibold">
          {icon ? <i className={`${icon} me-2`}></i> : null}
          {title}
        </h6>
        {action}
      </div>
      {entries.length === 0 ? (
        <small className="text-muted">{emptyText}</small>
      ) : (
        <div className="d-grid gap-3">
          {entries.map((entry, index) => (
            <div key={`${title}-${index}`} className="border rounded p-3">
              {renderEntry(entry, index)}
            </div>
          ))}
        </div>
      )}
    </div>
  </div>
)

const EmployeeProfile = () => {
  const { id } = useParams()
  const { data: response, isLoading, refetch } = useEmployee(id)
  const { data: showroomsResponse } = useShowrooms(
    { all: true, active: true },
    true
  )
  const { data: designationsResponse } = useAllEmployeeDesignations()
  const { data: departmentsResponse } = useAllEmployeeDepartments()
  const { mutateAsync: createDesignation } = useCreateEmployeeDesignation()
  const { mutateAsync: createDepartment } = useCreateEmployeeDepartment()
  const { data: supervisorsResponse } = useEmployees({ all: true }, true)

  const { hasAnyPermission } = useAuth()
  const canViewActivity = hasAnyPermission([
    ACCESS.EMPLOYEE.VIEW_ACTIVITY_LOG,
    ACCESS.ACTIVITY_LOG.VIEW_LIST,
  ])
  const canViewLoans = hasAnyPermission([ACCESS.ACCOUNT_LOAN.VIEW_LIST])

  const employee = useMemo(() => response?.data ?? null, [response])
  const [params, updateParams] = useUrlParams({ tab: "employment" })
  const allowedTabs = useMemo(() => {
    const tabs = [...EMPLOYEE_PROFILE_BASE_TABS]
    if (canViewLoans) tabs.push("loans")
    if (canViewActivity) tabs.push("activity")
    return tabs
  }, [canViewActivity, canViewLoans])
  const activeTab = allowedTabs.includes(params.tab)
    ? params.tab
    : "employment"

  const statusMeta = STATUS_META[employee?.status] || {
    label: humanize(employee?.status || "active"),
    color: "secondary",
  }

  const showrooms = useMemo(
    () => extractList(showroomsResponse),
    [showroomsResponse]
  )
  const designations = useMemo(
    () => extractList(designationsResponse),
    [designationsResponse]
  )
  const departments = useMemo(
    () => extractList(departmentsResponse),
    [departmentsResponse]
  )
  const supervisors = useMemo(
    () => extractList(supervisorsResponse),
    [supervisorsResponse]
  )

  const showroomOptions = useMemo(
    () =>
      showrooms.map(item => ({
        value: String(item.id),
        label: item.short_name
          ? `${item.name} (${item.short_name})`
          : item.name,
      })),
    [showrooms]
  )

  const designationOptions = useMemo(() => {
    const opts = designations.map(item => ({
      value: String(item.id),
      label: item.title || item.name,
    }))
    if (employee?.designation_id && employee?.designation) {
      const exists = opts.some(o => o.value === String(employee.designation_id))
      if (!exists) {
        opts.unshift({
          value: String(employee.designation_id),
          label: employee.designation.title || employee.designation.name,
        })
      }
    }
    return opts
  }, [designations, employee])

  const departmentOptions = useMemo(() => {
    const opts = departments.map(item => ({
      value: String(item.id),
      label: item.name,
    }))
    if (employee?.department_id && employee?.department) {
      const exists = opts.some(o => o.value === String(employee.department_id))
      if (!exists) {
        opts.unshift({
          value: String(employee.department_id),
          label: employee.department.name,
        })
      }
    }
    return opts
  }, [departments, employee])

  const supervisorOptions = useMemo(
    () =>
      supervisors
        .filter(item => String(item.id) !== String(employee?.id))
        .map(item => ({
          value: String(item.id),
          label: item.employee_id
            ? `${item.name} (#${item.employee_id})`
            : item.name,
        })),
    [supervisors, employee?.id]
  )

  const emergencyContacts = asCollection(employee?.emergency_contact).filter(
    Boolean
  )
  const education = asCollection(employee?.educational_qualifications).filter(
    Boolean
  )
  const references = asCollection(employee?.references).filter(Boolean)
  const lastOffice = asCollection(employee?.last_office).filter(Boolean)

  document.title = `Employee Profile${
    employee?.name ? ` | ${employee.name}` : ""
  }`

  const toggleTab = tab => {
    if (activeTab !== tab) updateParams({ tab })
  }

  const showroomData = prepareDataWithLoading(
    [
      { label: "Showroom Name", value: employee?.showroom?.name },
      { label: "Short Name", value: employee?.showroom?.short_name },
      { label: "Address", value: employee?.showroom?.address },
      { label: "Mobile", value: employee?.showroom?.mobile },
      { label: "Email", value: employee?.showroom?.email },
      { label: "Website", value: employee?.showroom?.website },
      {
        label: "Status",
        value: employee?.showroom?.is_active
          ? "Active"
          : employee?.showroom
          ? "Inactive"
          : "-",
      },
    ],
    isLoading
  )

  const compensationData = prepareDataWithLoading(
    [
      { label: "Basic Salary", value: formatMoney(employee?.basic_salary) },
      { label: "Gross Salary", value: formatMoney(employee?.gross_salary) },
      { label: "House Rent", value: formatMoney(employee?.house_rent) },
      {
        label: "Medical Allowance",
        value: formatMoney(employee?.medical_allowance),
      },
      {
        label: "Transport Allowance",
        value: formatMoney(employee?.transport_allowance),
      },
      {
        label: "Other Allowance",
        value: formatMoney(employee?.other_allowance),
      },
    ],
    isLoading
  )

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs
          title="Employee"
          breadcrumbItem="Profile"
          loading={isLoading}
        />

        <Row className="mb-4">
          <Col lg="12">
            <EmployeeInfo employee={employee} loading={isLoading} />
            {!isLoading && employee ? (
              <>
                <div className="badge badge-soft-primary mt-2">
                  <i
                    className="fas fa-map-marker-alt me-1"
                    style={{ fontSize: 7 }}
                  ></i>
                  {employee?.showroom?.name ?? ""}
                  {employee?.showroom?.short_name
                    ? ` - ${employee.showroom.short_name}`
                    : ""}
                </div>
                <div className="text-muted d-block mt-1 text-wrap">
                  <Badge className={`badge-soft-${statusMeta.color}`}>
                    {statusMeta.label}
                  </Badge>
                </div>
              </>
            ) : null}
          </Col>
        </Row>
        {employee && (
          <Row>
            <Col lg="4" className="mb-4">
              <EmployeeProfileCard
                employee={employee}
                statusMeta={statusMeta}
                loading={isLoading}
                action={
                  <EmployeeSectionUpdate
                    employee={employee}
                    title="Edit Employee Profile"
                    btnId="edit-employee-profile-card"
                    btnName="Edit Profile"
                    btnStyle={{
                      backgroundColor: "rgba(85,110,230,.1)",
                      color: "#495057",
                      borderColor: "rgba(85,110,230,.16)",
                    }}
                    onSaved={refetch}
                    fields={[
                      {
                        name: "name",
                        label: "Full Name",
                        type: "text",
                        value: employee.name,
                        required: true,
                        col: 6,
                      },
                      {
                        name: "mobile",
                        label: "Mobile",
                        type: "text",
                        value: employee.mobile,
                        col: 6,
                      },
                      {
                        name: "email",
                        label: "Email",
                        type: "email",
                        value: employee.email,
                        col: 6,
                      },
                      {
                        name: "designation_id",
                        label: "Designation",
                        type: "creatable",
                        value: employee.designation_id
                          ? String(employee.designation_id)
                          : "",
                        options: designationOptions,
                        col: 6,
                        onCreateOption: async inputValue => {
                          try {
                            const res = await createDesignation({
                              title: inputValue,
                            })
                            return res?.data?.id
                          } catch {
                            return undefined
                          }
                        },
                      },
                      {
                        name: "department_id",
                        label: "Department",
                        type: "creatable",
                        value: employee.department_id
                          ? String(employee.department_id)
                          : "",
                        options: departmentOptions,
                        col: 6,
                        onCreateOption: async inputValue => {
                          try {
                            const res = await createDepartment({
                              name: inputValue,
                            })
                            return res?.data?.id
                          } catch {
                            return undefined
                          }
                        },
                      },
                      {
                        name: "joining_date",
                        label: "Joining Date",
                        type: "date",
                        value: employee.joining_date
                          ? String(employee.joining_date).slice(0, 10)
                          : "",
                        required: true,
                        col: 6,
                      },
                    ]}
                  />
                }
              />

              <div className="mb-4">
                <DataCard
                  title="Showroom Information"
                  icon="bx bx-building-house"
                  data={showroomData}
                  action={
                    <EmployeeSectionUpdate
                      employee={employee}
                      title="Change Showroom Assignment"
                      btnId="edit-showroom-assignment"
                      btnName="Change Showroom"
                      onSaved={refetch}
                      fields={[
                        {
                          name: "showroom_id",
                          label: "Showroom",
                          type: "select",
                          value: employee.showroom_id
                            ? String(employee.showroom_id)
                            : "",
                          options: showroomOptions,
                          required: true,
                          col: 12,
                        },
                      ]}
                    />
                  }
                />
              </div>

              <div className="mb-4">
                <DataCard
                  title="Compensation Snapshot"
                  icon="bx bx-wallet"
                  data={compensationData}
                  action={
                    <EmployeeSectionUpdate
                      employee={employee}
                      title="Edit Compensation"
                      btnId="edit-employee-compensation"
                      btnName="Edit Compensation"
                      onSaved={refetch}
                      fields={[
                        {
                          name: "basic_salary",
                          label: "Basic Salary",
                          type: "number",
                          value: employee.basic_salary,
                          col: 6,
                        },
                        {
                          name: "gross_salary",
                          label: "Gross Salary",
                          type: "number",
                          value: employee.gross_salary,
                          col: 6,
                        },
                        {
                          name: "house_rent",
                          label: "House Rent",
                          type: "number",
                          value: employee.house_rent,
                          col: 6,
                        },
                        {
                          name: "medical_allowance",
                          label: "Medical Allowance",
                          type: "number",
                          value: employee.medical_allowance,
                          col: 6,
                        },
                        {
                          name: "transport_allowance",
                          label: "Transport Allowance",
                          type: "number",
                          value: employee.transport_allowance,
                          col: 6,
                        },
                        {
                          name: "other_allowance",
                          label: "Other Allowance",
                          type: "number",
                          value: employee.other_allowance,
                          col: 6,
                        },
                      ]}
                    />
                  }
                />
              </div>
            </Col>

            <Col lg="8">
              <Nav pills className="nav-pills-custom mb-4">
                {[
                  {
                    id: "employment",
                    label: "Employment",
                    icon: "bx bx-briefcase",
                  },
                  { id: "personal", label: "Personal", icon: "bx bx-user" },
                  {
                    id: "contacts",
                    label: "Contacts & Records",
                    icon: "bx bx-detail",
                  },
                  {
                    id: "access",
                    label: "System Access",
                    icon: "bx bx-lock-open",
                  },
                  ...(canViewLoans && employee?.user_id
                    ? [
                        {
                          id: "loans",
                          label: "Loans",
                          icon: "bx bx-money",
                        },
                      ]
                    : []),
                  ...(canViewActivity
                    ? [
                        {
                          id: "activity",
                          label: "Activity Log",
                          icon: "bx bx-history",
                        },
                      ]
                    : []),
                ].map(tab => (
                  <NavItem key={tab.id}>
                    <NavLink
                      className={classnames({ active: activeTab === tab.id })}
                      onClick={() => toggleTab(tab.id)}
                    >
                      <i className={`${tab.icon} me-2`}></i>
                      {tab.label}
                    </NavLink>
                  </NavItem>
                ))}
              </Nav>

              <TabContent activeTab={activeTab}>
                <TabPane tabId="employment">
                  <Row>
                    <Col md="6" className="mb-4">
                      <DataCard
                        title="Employment Timeline"
                        icon="bx bx-calendar-event"
                        data={prepareDataWithLoading(
                          [
                            {
                              label: "Employment Type",
                              value: humanize(employee.employment_type),
                            },
                            {
                              label: "Joining Date",
                              value: formatDateValue(employee.joining_date),
                            },
                            {
                              label: "Confirmation Date",
                              value: formatDateValue(
                                employee.confirmation_date
                              ),
                            },
                            {
                              label: "Resign Date",
                              value: formatDateValue(employee.resign_date),
                            },
                            {
                              label: "Resign Reason",
                              value: employee.resign_reason,
                            },
                            { label: "Notes", value: employee.notes },
                          ],
                          isLoading
                        )}
                        action={
                          <EmployeeSectionUpdate
                            employee={employee}
                            title="Edit Employment Timeline"
                            btnId="edit-employment-timeline"
                            btnName="Edit Employment"
                            onSaved={refetch}
                            fields={[
                              {
                                name: "employment_type",
                                label: "Employment Type",
                                type: "select",
                                value: employee.employment_type,
                                options: EMPLOYMENT_TYPE_OPTIONS,
                                col: 6,
                              },
                              {
                                name: "joining_date",
                                label: "Joining Date",
                                type: "date",
                                value: employee.joining_date
                                  ? String(employee.joining_date).slice(0, 10)
                                  : "",
                                required: true,
                                col: 6,
                              },
                              {
                                name: "status",
                                label: "Status",
                                type: "select",
                                value: employee.status || "active",
                                options: STATUS_OPTIONS,
                                required: true,
                                helpText:
                                  "If status is Resigned, resign date and reason are required.",
                                col: 6,
                              },
                              {
                                name: "confirmation_date",
                                label: "Confirmation Date",
                                type: "date",
                                value: employee.confirmation_date
                                  ? String(employee.confirmation_date).slice(
                                      0,
                                      10
                                    )
                                  : "",
                                col: 6,
                              },
                              {
                                name: "resign_date",
                                label: "Resign Date",
                                type: "date",
                                value: employee.resign_date
                                  ? String(employee.resign_date).slice(0, 10)
                                  : "",
                                showWhen: values => values.status === "resigned",
                                col: 6,
                              },
                              {
                                name: "resign_reason",
                                label: "Resign Reason",
                                type: "textarea",
                                value: employee.resign_reason,
                                showWhen: values => values.status === "resigned",
                                rows: 3,
                                col: 12,
                              },
                              {
                                name: "notes",
                                label: "Notes",
                                type: "textarea",
                                value: employee.notes,
                                rows: 4,
                                col: 12,
                              },
                            ]}
                          />
                        }
                      />
                    </Col>

                    <Col md="6" className="mb-4">
                      <DataCard
                        title="Bank Information"
                        icon="bx bx-credit-card"
                        data={prepareDataWithLoading(
                          getBankInfoData(employee.bank_info),
                          isLoading
                        )}
                        noDataText="No bank information available"
                        action={
                          <EmployeeSectionUpdate
                            employee={employee}
                            title="Edit Bank Information"
                            btnId="edit-bank-info"
                            btnName="Edit Bank Info"
                            onSaved={refetch}
                            transformPayload={buildBankInfoPayload}
                            fields={[
                              {
                                name: "bank_name",
                                label: "Bank Name",
                                type: "text",
                                value: employee.bank_info?.bank_name,
                                col: 6,
                              },
                              {
                                name: "branch_name",
                                label: "Branch Name",
                                type: "text",
                                value: employee.bank_info?.branch_name,
                                col: 6,
                              },
                              {
                                name: "account_name",
                                label: "Account Name",
                                type: "text",
                                value: employee.bank_info?.account_name,
                                col: 6,
                              },
                              {
                                name: "account_number",
                                label: "Account Number",
                                type: "text",
                                value: employee.bank_info?.account_number,
                                col: 6,
                              },
                              {
                                name: "routing_number",
                                label: "Routing Number",
                                type: "text",
                                value: employee.bank_info?.routing_number,
                                col: 6,
                              },
                              {
                                name: "swift_code",
                                label: "SWIFT Code",
                                type: "text",
                                value: employee.bank_info?.swift_code,
                                col: 6,
                              },
                            ]}
                          />
                        }
                      />
                    </Col>
                  </Row>
                </TabPane>

                <TabPane tabId="personal">
                  <Row>
                    <Col md="6" className="mb-4">
                      <DataCard
                        title="Personal Details"
                        icon="bx bx-user-circle"
                        data={prepareDataWithLoading(
                          [
                            {
                              label: "Father Name",
                              value: employee.father_name,
                            },
                            {
                              label: "Mother Name",
                              value: employee.mother_name,
                            },
                            {
                              label: "Date of Birth",
                              value: formatDateValue(employee.date_of_birth),
                            },
                            {
                              label: "Gender",
                              value: humanize(employee.gender),
                            },
                            {
                              label: "Blood Group",
                              value: employee.blood_group,
                            },
                            { label: "Religion", value: employee.religion },
                          ],
                          isLoading
                        )}
                        action={
                          <EmployeeSectionUpdate
                            employee={employee}
                            title="Edit Personal Details"
                            btnId="edit-personal-details"
                            btnName="Edit Personal"
                            onSaved={refetch}
                            fields={[
                              {
                                name: "father_name",
                                label: "Father Name",
                                type: "text",
                                value: employee.father_name,
                                col: 6,
                              },
                              {
                                name: "mother_name",
                                label: "Mother Name",
                                type: "text",
                                value: employee.mother_name,
                                col: 6,
                              },
                              {
                                name: "date_of_birth",
                                label: "Date of Birth",
                                type: "date",
                                value: employee.date_of_birth
                                  ? String(employee.date_of_birth).slice(0, 10)
                                  : "",
                                col: 6,
                              },
                              {
                                name: "gender",
                                label: "Gender",
                                type: "select",
                                value: employee.gender,
                                options: GENDER_OPTIONS,
                                col: 6,
                              },
                              {
                                name: "blood_group",
                                label: "Blood Group",
                                type: "select",
                                value: employee.blood_group,
                                options: BLOOD_GROUP_OPTIONS,
                                col: 6,
                              },
                              {
                                name: "religion",
                                label: "Religion",
                                type: "select",
                                value: employee.religion,
                                options: RELIGION_OPTIONS,
                                col: 6,
                              },
                            ]}
                          />
                        }
                      />
                    </Col>

                    <Col md="6" className="mb-4">
                      <DataCard
                        title="Family & Mobility"
                        icon="bx bx-home-heart"
                        data={prepareDataWithLoading(
                          [
                            {
                              label: "Marital Status",
                              value: humanize(employee.marital_status),
                            },
                            {
                              label: "Spouse Name",
                              value: employee.spouse_name,
                            },
                            {
                              label: "Number of Children",
                              value: employee.number_of_children,
                            },
                            {
                              label: "Can Drive",
                              value: formatBoolean(employee.can_drive),
                            },
                            {
                              label: "Own Transport",
                              value: formatBoolean(employee.has_own_transport),
                            },
                          ],
                          isLoading
                        )}
                        action={
                          <EmployeeSectionUpdate
                            employee={employee}
                            title="Edit Family & Mobility"
                            btnId="edit-family-mobility"
                            btnName="Edit Family"
                            onSaved={refetch}
                            transformPayload={payload => ({
                              ...payload,
                              can_drive: payload.can_drive === "1",
                              has_own_transport:
                                payload.has_own_transport === "1",
                            })}
                            fields={[
                              {
                                name: "marital_status",
                                label: "Marital Status",
                                type: "select",
                                value: employee.marital_status,
                                options: MARITAL_STATUS_OPTIONS,
                                col: 6,
                              },
                              {
                                name: "spouse_name",
                                label: "Spouse Name",
                                type: "text",
                                value: employee.spouse_name,
                                col: 6,
                              },
                              {
                                name: "number_of_children",
                                label: "Number of Children",
                                type: "number",
                                value: employee.number_of_children,
                                col: 6,
                              },
                              {
                                name: "can_drive",
                                label: "Can Drive",
                                type: "select",
                                value: employee.can_drive ? "1" : "0",
                                options: YES_NO_OPTIONS,
                                col: 6,
                              },
                              {
                                name: "has_own_transport",
                                label: "Has Own Transport",
                                type: "select",
                                value: employee.has_own_transport ? "1" : "0",
                                options: YES_NO_OPTIONS,
                                col: 6,
                              },
                            ]}
                          />
                        }
                      />
                    </Col>
                  </Row>
                </TabPane>

                <TabPane tabId="contacts">
                  <Row>
                    <Col lg="12" className="mb-4">
                      <DataCard
                        title="Address Information"
                        icon="bx bx-map"
                        data={prepareDataWithLoading(
                          [
                            {
                              label: "Current Address",
                              value: employee.current_address,
                            },
                            {
                              label: "Permanent Address",
                              value: employee.permanent_address,
                            },
                            { label: "Home Town", value: employee.home_town },
                          ],
                          isLoading
                        )}
                        action={
                          <EmployeeSectionUpdate
                            employee={employee}
                            title="Edit Address Information"
                            btnId="edit-address-information"
                            btnName="Edit Address"
                            onSaved={refetch}
                            fields={[
                              {
                                name: "current_address",
                                label: "Current Address",
                                type: "textarea",
                                value: employee.current_address,
                                rows: 3,
                                col: 12,
                              },
                              {
                                name: "permanent_address",
                                label: "Permanent Address",
                                type: "textarea",
                                value: employee.permanent_address,
                                rows: 3,
                                col: 12,
                              },
                              {
                                name: "home_town",
                                label: "Home Town",
                                type: "text",
                                value: employee.home_town,
                                col: 12,
                              },
                            ]}
                          />
                        }
                      />
                    </Col>

                    <Col lg="12" className="mb-4">
                      <RichCollectionCard
                        title="Emergency Contacts"
                        icon="bx bx-first-aid"
                        entries={emergencyContacts}
                        emptyText="No emergency contacts available"
                        action={
                          <EmployeeSectionUpdate
                            employee={employee}
                            title="Edit Emergency Contacts"
                            btnId="edit-emergency-contacts"
                            btnName="Edit Contacts"
                            onSaved={refetch}
                            fields={[
                              {
                                name: "emergency_contact",
                                label: "Emergency Contacts",
                                type: "repeater",
                                value: employee.emergency_contact,
                                col: 12,
                                subFields: [
                                  { name: "name", label: "Name", col: 6 },
                                  {
                                    name: "relation",
                                    label: "Relation",
                                    col: 6,
                                  },
                                  { name: "mobile", label: "Mobile", col: 6 },
                                  { name: "type", label: "Type", col: 6 },
                                  {
                                    name: "address",
                                    label: "Address",
                                    type: "textarea",
                                    col: 12,
                                  },
                                ],
                              },
                            ]}
                          />
                        }
                        renderEntry={entry => (
                          <Row>
                            <Col md="6" className="mb-2">
                              <strong>Name:</strong> {entry?.name || "-"}
                            </Col>
                            <Col md="6" className="mb-2">
                              <strong>Relation:</strong>{" "}
                              {entry?.relation || "-"}
                            </Col>
                            <Col md="6" className="mb-2">
                              <strong>Mobile:</strong> {entry?.mobile || "-"}
                            </Col>
                            <Col md="6" className="mb-2">
                              <strong>Type:</strong> {entry?.type || "-"}
                            </Col>
                            <Col md="12">
                              <strong>Address:</strong> {entry?.address || "-"}
                            </Col>
                          </Row>
                        )}
                      />
                    </Col>

                    <Col md="6" className="mb-4">
                      <RichCollectionCard
                        title="Educational Qualifications"
                        icon="bx bx-book"
                        entries={education}
                        emptyText="No educational qualifications available"
                        action={
                          <EmployeeSectionUpdate
                            employee={employee}
                            title="Edit Educational Qualifications"
                            btnId="edit-education"
                            btnName="Edit Education"
                            onSaved={refetch}
                            fields={[
                              {
                                name: "educational_qualifications",
                                label: "Educational Qualifications",
                                type: "repeater",
                                value: employee.educational_qualifications,
                                col: 12,
                                subFields: [
                                  {
                                    name: "degree",
                                    label: "Degree / Level",
                                    col: 6,
                                  },
                                  {
                                    name: "institution",
                                    label: "Institution",
                                    col: 6,
                                  },
                                  {
                                    name: "board",
                                    label: "Board / University",
                                    col: 6,
                                  },
                                  {
                                    name: "passing_year",
                                    label: "Passing Year",
                                    col: 3,
                                  },
                                  {
                                    name: "result",
                                    label: "Result / Grade",
                                    col: 3,
                                  },
                                ],
                              },
                            ]}
                          />
                        }
                        renderEntry={entry => (
                          <Row>
                            <Col md="6" className="mb-2">
                              <strong>Degree:</strong> {entry?.degree || "-"}
                            </Col>
                            <Col md="6" className="mb-2">
                              <strong>Institution:</strong>{" "}
                              {entry?.institution || "-"}
                            </Col>
                            <Col md="6" className="mb-2">
                              <strong>Board:</strong> {entry?.board || "-"}
                            </Col>
                            <Col md="3" className="mb-2">
                              <strong>Year:</strong>{" "}
                              {entry?.passing_year || "-"}
                            </Col>
                            <Col md="3" className="mb-2">
                              <strong>Result:</strong> {entry?.result || "-"}
                            </Col>
                          </Row>
                        )}
                      />
                    </Col>

                    <Col md="6" className="mb-4">
                      <RichCollectionCard
                        title="References"
                        icon="bx bx-group"
                        entries={references}
                        emptyText="No references available"
                        action={
                          <EmployeeSectionUpdate
                            employee={employee}
                            title="Edit References"
                            btnId="edit-references"
                            btnName="Edit References"
                            onSaved={refetch}
                            fields={[
                              {
                                name: "references",
                                label: "References",
                                type: "repeater",
                                value: employee.references,
                                col: 12,
                                subFields: [
                                  { name: "name", label: "Name", col: 6 },
                                  {
                                    name: "designation",
                                    label: "Designation",
                                    col: 6,
                                  },
                                  {
                                    name: "office",
                                    label: "Office / Organization",
                                    col: 6,
                                  },
                                  {
                                    name: "relation",
                                    label: "Relation",
                                    col: 6,
                                  },
                                  { name: "mobile", label: "Mobile", col: 6 },
                                  { name: "address", label: "Address", col: 6 },
                                ],
                              },
                            ]}
                          />
                        }
                        renderEntry={entry => (
                          <Row>
                            <Col md="6" className="mb-2">
                              <strong>Name:</strong> {entry?.name || "-"}
                            </Col>
                            <Col md="6" className="mb-2">
                              <strong>Designation:</strong>{" "}
                              {entry?.designation || "-"}
                            </Col>
                            <Col md="6" className="mb-2">
                              <strong>Office:</strong> {entry?.office || "-"}
                            </Col>
                            <Col md="6" className="mb-2">
                              <strong>Relation:</strong>{" "}
                              {entry?.relation || "-"}
                            </Col>
                            <Col md="6" className="mb-2">
                              <strong>Mobile:</strong> {entry?.mobile || "-"}
                            </Col>
                            <Col md="6" className="mb-2">
                              <strong>Address:</strong> {entry?.address || "-"}
                            </Col>
                          </Row>
                        )}
                      />
                    </Col>

                    <Col lg="12" className="mb-4">
                      <RichCollectionCard
                        title="Previous Offices"
                        icon="bx bx-history"
                        entries={lastOffice}
                        emptyText="No previous office records available"
                        action={
                          <EmployeeSectionUpdate
                            employee={employee}
                            title="Edit Previous Offices"
                            btnId="edit-previous-offices"
                            btnName="Edit Offices"
                            onSaved={refetch}
                            fields={[
                              {
                                name: "last_office",
                                label: "Previous Offices",
                                type: "repeater",
                                value: employee.last_office,
                                col: 12,
                                subFields: [
                                  {
                                    name: "company_name",
                                    label: "Company Name",
                                    col: 6,
                                  },
                                  {
                                    name: "designation",
                                    label: "Designation",
                                    col: 6,
                                  },
                                  {
                                    name: "department",
                                    label: "Department",
                                    col: 6,
                                  },
                                  {
                                    name: "from_date",
                                    label: "From Date",
                                    col: 3,
                                  },
                                  { name: "to_date", label: "To Date", col: 3 },
                                  {
                                    name: "reason_of_leaving",
                                    label: "Reason of Leaving",
                                    col: 12,
                                  },
                                ],
                              },
                            ]}
                          />
                        }
                        renderEntry={entry => (
                          <Row>
                            <Col md="6" className="mb-2">
                              <strong>Company:</strong>{" "}
                              {entry?.company_name || "-"}
                            </Col>
                            <Col md="6" className="mb-2">
                              <strong>Designation:</strong>{" "}
                              {entry?.designation || "-"}
                            </Col>
                            <Col md="6" className="mb-2">
                              <strong>Department:</strong>{" "}
                              {entry?.department || "-"}
                            </Col>
                            <Col md="3" className="mb-2">
                              <strong>From:</strong> {entry?.from_date || "-"}
                            </Col>
                            <Col md="3" className="mb-2">
                              <strong>To:</strong> {entry?.to_date || "-"}
                            </Col>
                            {entry?.reason_of_leaving ? (
                              <Col md="12">
                                <strong>Reason:</strong>{" "}
                                {entry.reason_of_leaving}
                              </Col>
                            ) : null}
                          </Row>
                        )}
                      />
                    </Col>
                  </Row>
                </TabPane>

                <TabPane tabId="access">
                  <Row>
                    <Col lg="12" className="mb-4">
                      <SystemAccessCard
                        employee={employee}
                        onChange={refetch}
                      />
                    </Col>
                  </Row>
                </TabPane>

                {canViewLoans && employee?.user_id && (
                  <TabPane tabId="loans">
                    <Row>
                      <Col lg="12" className="mb-4">
                        {activeTab === "loans" ? (
                          <LoanSummaryCard borrowerType="staff" borrowerId={employee.user_id} />
                        ) : null}
                      </Col>
                    </Row>
                  </TabPane>
                )}

                {canViewActivity && (
                  <TabPane tabId="activity">
                    <Row>
                      <Col lg="12" className="mb-4">
                        <ActivityTab employee={employee} />
                      </Col>
                    </Row>
                  </TabPane>
                )}
              </TabContent>
            </Col>
          </Row>
        )}
      </Container>
    </div>
  )
}

export default EmployeeProfile
